/**
 * Zubehoer (accessory) selection persistence using localStorage.
 *
 * Stores selected accessory IDs per product (article type) so that
 * selections survive page reloads and browser back/forward navigation.
 * Keys are cleared when the product is added to the basket.
 */

/**
 * Returns the localStorage key for storing accessory selections of the current product.
 * The key is based on the article type ID (atId) set on the PDP.
 * @returns {string|null} e.g. 'zubehoer_123' or null if atId is not available
 */
export function getZubehoerStorageKey() {
  if (typeof atId === 'undefined') {
    return null;
  }
  return 'zubehoer_' + atId;
}

/**
 * Persists the currently active accessory IDs to localStorage.
 * Stores them as a JSON array under the key 'zubehoer_<atId>'.
 * If no accessories are selected, the key is removed.
 */
export function saveZubehoerSelection() {
  const key = getZubehoerStorageKey();
  if (!key) {
    return;
  }

  const selectedIds = [];
  document.querySelectorAll('.js_buybox_equipment_item.active').forEach(function (el) {
    selectedIds.push(el.dataset.id);
  });

  if (selectedIds.length > 0) {
    localStorage.setItem(key, JSON.stringify(selectedIds));
  } else {
    localStorage.removeItem(key);
  }
}

/**
 * Restores previously selected accessories from localStorage on page load.
 *
 * Two scenarios:
 * 1. URL contains sel_zub[] params (internal navigation via historyManager):
 *    Accessories are already activated by PHP, so we only sync them to localStorage.
 * 2. No sel_zub[] params (page reload or browser back/forward):
 *    Re-activate accessories from localStorage by calling toggleZubehoer() for each stored ID.
 */
export function restoreZubehoerSelection() {
  const key = getZubehoerStorageKey();
  if (!key) {
    return;
  }

  // If sel_zub params are in the URL, accessories are already active via PHP rendering.
  // Just persist the current state to localStorage so it survives the next reload.
  const urlParams = new URLSearchParams(window.location.search);
  const hasSelZubInUrl = urlParams.has('sel_zub[0]');
  if (hasSelZubInUrl) {
    saveZubehoerSelection();
    return;
  }

  // Read stored accessory IDs from localStorage
  const stored = localStorage.getItem(key);
  if (!stored) {
    return;
  }

  let selectedIds;
  try {
    selectedIds = JSON.parse(stored);
  } catch (e) {
    // Corrupted data — clean up and abort
    localStorage.removeItem(key);
    return;
  }

  if (!Array.isArray(selectedIds) || selectedIds.length === 0) {
    return;
  }

  // Re-activate each stored accessory that is not already active.
  // toggleZubehoer() is defined globally in modell-no-module.js and handles
  // all side effects (DOM update, price recalculation, etc.)
  selectedIds.forEach(function (id) {
    const el = document.querySelector('.js_buybox_equipment_item.mo_zubehoer_' + id);
    if (el && !el.classList.contains('active')) {
      window.toggleZubehoer(id);
    }
  });
}

/**
 * Removes only the current product's accessory selection key from localStorage.
 * Called after the product (with its accessories) has been added to the basket,
 * so this specific product's selection is no longer needed.
 * Other products' accessory selections are preserved.
 */
export function clearCurrentZubehoerStorage() {
  const key = getZubehoerStorageKey();
  if (key) {
    localStorage.removeItem(key);
  }
}

/**
 * Removes all accessory selection keys ('zubehoer_*') from localStorage.
 * Available as a utility but not called automatically.
 */
export function clearZubehoerStorage() {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('zubehoer_')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(function (key) {
    localStorage.removeItem(key);
  });
}
